import React, { useState } from 'react';
import PropTypes from 'prop-types';

import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { alpha } from '@mui/material/styles';

import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';

import PlusIcon from '@mui/icons-material/Add';

import ColumnChooser from './ColumnChooser';
import DownloadIcon from '@mui/icons-material/CloudDownload';

const styles = (theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    toolBar: {
      backgroundColor: '#cdf',
      minWidth: 702,
    },
    grow: {
      flexGrow: 1,
    },
    newItemButton: {
      margin: theme.spacing(1),
    },
    menuButton: {
      marginLeft: -12,
      marginRight: 20,
    },
    title: {
      display: 'none',
      [theme.breakpoints.up('sm')]: {
        display: 'block',
      },
      fontFamily: 'MetricWebBold, sans-serif',
    },
    search: {
      backgroundColor: alpha(theme.palette.common.white, 0.2),
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.3),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#cccccc !important',
        color: '#888888',
      },
      '& .Mui-focused': {
        color: '#999999',
      },
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
      },
    },
    searchIcon: {
      width: theme.spacing(9),
      height: '100%',
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    inputRoot: {
      color: 'inherit',
      width: '100%',
    },
    inputInput: {
      border: 0,
      color: 'currentColor',
      boxSizing: 'content-box',
      background: 'none',
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(10),
      transition: theme.transitions.create('width'),
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: 120,
        '&:focus': {
          width: 200,
        },
      },
    },
    iconDiv: {
      marginTop: '10px',
      marginRight: '20px',
    },
    downloadIcon: {
      display: 'inline-block',
      marginLeft: '32px',
      '&:hover': {
        color: '#3a3',
      },
    },
    downloadIcon_grayed: {
      display: 'inline-block',
      marginLeft: '32px',
      color: '#aaa',
    },
  });

const uniquifyOptions = (options) => {
  // First identify duplicates in the options list (duplicated preferredFullName)
  let names = [];
  let uniqueOptions = [];
  options.forEach((opt) => {
    const pName = opt.preferredFullName;
    if (names.indexOf(pName) < 0) {
      uniqueOptions = [...uniqueOptions, opt];
      names = [...names, pName];
    }
  });
  return uniqueOptions;
};

function ClientTableAppBar(props) {
  const {
    classes,
    summary,
    newButtonLabel,
    newButtonFunc,
    onAutocompleteChange,
    clients,
    hideableColumnNamesAndCheckedState,
    onColumnToggle,
    onColumnReorder,
    downloadFunc,
    shownStatus,
  } = props;
  const [downloading, setDownloading] = useState(false);
  const uniqueOptions = uniquifyOptions(clients);

  const getTooltipText = (shownStatus) => {
    if (downloading) {
      return 'Downloading...';
    }
    switch (shownStatus) {
      case 'Prospect':
        return 'Download Shown Prospects';
      case 'Alum':
        return 'Download Shown Alumni';
      case 'Client':
      default:
        return 'Download Shown Clients';
    }
  };

  const tooltipText = getTooltipText(shownStatus);

  return (
    <div className={classes.root}>
      <Toolbar className={classes.toolBar}>
        <Typography className={classes.title} variant="h5" color="inherit" noWrap>
          {summary}
        </Typography>
        <div className={classes.grow} />
        {newButtonLabel && (
          <Button className={classes.newItemButton} onClick={newButtonFunc}>
            <PlusIcon />
            {newButtonLabel}
          </Button>
        )}
        <div className={classes.grow} />
        <div className={classes.search}>
          <Autocomplete
            options={uniqueOptions}
            getOptionLabel={(person) => person.preferredFullName}
            sx={{ width: 220 }}
            renderInput={(params) => <TextField variant="outlined" {...params} label="Search" />}
            onChange={(event, person) => onAutocompleteChange(person)}
          />
        </div>
        <div className={classes.grow} />
        <div className={classes.iconDiv}>
          <ColumnChooser
            title="Select/Reorder Columns"
            labelAndCheckedPairs={hideableColumnNamesAndCheckedState}
            onToggle={(label, checked) => onColumnToggle(label, checked)}
            onReorder={onColumnReorder}
          />
          <div
            className={downloading ? classes.downloadIcon_grayed : classes.downloadIcon}
            onClick={() => {
              if (downloading) {
                return;
              } else {
                setDownloading(true);
                downloadFunc(setDownloading);
              }
            }}
          >
            <Tooltip title={tooltipText}>
              <DownloadIcon />
            </Tooltip>
          </div>
        </div>
      </Toolbar>
    </div>
  );
}

ClientTableAppBar.propTypes = {
  classes: PropTypes.object.isRequired,
  summary: PropTypes.string.isRequired,
  clients: PropTypes.array.isRequired,
};

export default withStyles(styles)(ClientTableAppBar);
